import { useCurrentUser } from '@map/hooks/userApi';
import { allExperiencesSelector } from '@map/state/experiences';
import {
  acceptedExperienceSkillsSelector,
  experienceSkillsArraySelector,
} from '@map/state/experienceSkills';
import { useCurrentProfile } from '@map/state/profile';
import { Profile } from '@map/types';
import { CERTIFICATION_EDUCATION_TYPE } from '@shared/constants';
import useMountEffect from '@shared/hooks/useMountEffect';
import mixpanel from '@shared/lib/mixpanel/mixpanel';
import { debounce } from '@shared/util/function';
import { parseDate } from '@shared/util/parseDate';
import { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

const UNIVERSITY_PHD_EDUCATION_TYPE = 'university_phd';
const UNIVERSITY_MASTER_EDUCATION_TYPE = 'university_master';
const UNIVERSITY_BACHELOR_EDUCATION_TYPE = 'university_bachelor';
const HIGH_SCHOOL_EDUCATION_TYPE = 'high_school';
const VOCATIONAL_EDUCATION_TYPE = 'vocational';
const OTHER_EDUCATION_TYPE = 'other';

const getMotivationsProps = (motivations: Profile['motivations']) => {
  const key = 'motivation-';
  const data = {
    [`${key}see_skills`]: false,
    [`${key}get_job`]: false,
    [`${key}find_careers`]: false,
    [`${key}build_cv`]: false,
    [`${key}count`]: 0,
  };

  if (motivations) {
    motivations.forEach((m) => {
      if (m === 'skills') {
        data[`${key}see_skills`] = true;
      }
      if (m === 'job') {
        data[`${key}get_job`] = true;
      }
      if (m === 'careers') {
        data[`${key}find_careers`] = true;
      }
      if (m === 'cv') {
        data[`${key}build_cv`] = true;
      }
    });

    data[`${key}count`] = motivations.length;
  }

  return data;
};

const arrayLengthSelector = ({ length }: any[]) => length;

const totalReviewedSkillsCount = createSelector(experienceSkillsArraySelector, arrayLengthSelector);

const totalAcceptedSkillsCount = createSelector(
  acceptedExperienceSkillsSelector,
  arrayLengthSelector,
);

const totalExperiencesCount = createSelector(allExperiencesSelector, arrayLengthSelector);

const educationOrder = [
  OTHER_EDUCATION_TYPE,
  HIGH_SCHOOL_EDUCATION_TYPE,
  VOCATIONAL_EDUCATION_TYPE,
  CERTIFICATION_EDUCATION_TYPE,
  UNIVERSITY_BACHELOR_EDUCATION_TYPE,
  UNIVERSITY_MASTER_EDUCATION_TYPE,
  UNIVERSITY_PHD_EDUCATION_TYPE,
];

const highestEducationLevel = createSelector(allExperiencesSelector, (exps) =>
  Math.max(
    ...exps
      .filter((exp) => exp.category === 'education')
      .map((exp) => educationOrder.indexOf(exp.education_type)),
  ),
);

let allChanges = {};

const debouncedPeopleSet = debounce(() => {
  mixpanel.people.set(allChanges);
  allChanges = {};
}, 1000);

const enqueueUpdate = (properties: Record<string, any>) => {
  Object.assign(allChanges, properties);
  debouncedPeopleSet();
};

const MixpanelPropertyUpdater: FC = () => {
  const totalReviewedSkills = useSelector(totalReviewedSkillsCount);
  const totalAcceptedSkills = useSelector(totalAcceptedSkillsCount);
  const totalExperiences = useSelector(totalExperiencesCount);
  const highestEducationIndex = useSelector(highestEducationLevel);
  const profile = useCurrentProfile();
  const user = useCurrentUser();
  const age = useMemo(() => {
    const birthDate = parseDate(profile.birthday, true);
    if (birthDate) {
      return Math.floor((new Date().getTime() - birthDate.getTime()) / 86400000 / 356);
    }
  }, [profile.birthday]);
  const isPwa = useMemo(
    () => (navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches,
    [],
  );

  useEffect(() => {
    enqueueUpdate({ number_of_reviewed_skills: totalReviewedSkills });
  }, [totalReviewedSkills]);
  useEffect(() => {
    enqueueUpdate({ number_of_accepted_skills: totalAcceptedSkills });
  }, [totalAcceptedSkills]);
  useEffect(() => {
    enqueueUpdate({ number_of_experiences: totalExperiences });
  }, [, totalExperiences]);
  useEffect(() => {
    enqueueUpdate({ highest_education_level: educationOrder[highestEducationIndex] });
  }, [highestEducationIndex]);
  useEffect(() => {
    if (age) {
      enqueueUpdate({ age });
    }
  }, [age]);
  useEffect(() => {
    enqueueUpdate({ gender: profile.gender || 'not_specified' });
  }, [profile.gender]);
  useEffect(() => {
    enqueueUpdate({ ...getMotivationsProps(profile.motivations) });
  }, [profile.motivations]);
  useEffect(() => {
    enqueueUpdate({ locale: user.locale });
  }, [user.locale]);
  useEffect(() => {
    enqueueUpdate({
      project_id: profile.project_id || 0,
      project: profile.project_title || 'No project',
    });
  }, [profile.project_id, profile.project_title]);
  useEffect(() => {
    enqueueUpdate({ is_pwa: isPwa });
  }, [isPwa]);

  useMountEffect(() => {
    enqueueUpdate({
      number_of_reviewed_skills: totalReviewedSkills,
      number_of_accepted_skills: totalAcceptedSkills,
      number_of_experiences: totalExperiences,
      highest_education_level: educationOrder[highestEducationIndex],
      age,
      gender: profile.gender || 'not_specified',
      ...getMotivationsProps(profile.motivations),
      locale: user.locale,
      is_pwa: isPwa,
      project_id: profile.project_id || 0,
      project: profile.project_title || 'No project',
    });
  });

  return null;
};

export default memo(MixpanelPropertyUpdater);
